import type { ReactNode } from "react";
import type { ActionFunction, LoaderFunction } from "@remix-run/node";
import { redirect, useActionData, useLoaderData } from "@remix-run/react";
import * as Sentry from "@sentry/remix";
import { LoginForm } from "~/components/forms/login-form";
import { authenticator } from "~/services/authenticator";
import { sessionStorage } from "~/services/session.server";

type LoaderData = {
  passwordLoginEnabled: boolean;
  strongAuthErrorMessage?: string;
  apiUrl: string;
};

function strongAuthErrorMessage(code: string | null): string | undefined {
  if (!code) {
    return undefined;
  }

  if (code === "customer_not_found" || code === "employee_not_found") {
    return "Tunnistautuminen onnistui, mutta käyttäjätiliä ei löydy järjestelmästä. Ole yhteydessä tukeen.";
  }
  if (code === "cancelled") {
    return "Tunnistautuminen keskeytettiin. Yritä uudelleen.";
  }

  return "Tunnistautumisessa tapahtui virhe. Yritä uudelleen.";
}

function isPasswordLoginEnabled(): boolean {
  // Keep default behavior as enabled unless explicitly disabled.
  return process.env.PASSWORD_LOGIN_ENABLED !== "false";
}

export const action: ActionFunction = async ({ request }) => {
  if (!isPasswordLoginEnabled()) {
    throw redirect("/strong-auth/employee");
  }

  try {
    const user = await authenticator.authenticate("user-pass", request);

    if (!user?.id) {
      return { error: "Virheellinen sähköposti tai salasana" };
    }

    const session = await sessionStorage.getSession(
      request.headers.get("cookie")
    );
    session.set("user", user);

    let redirectUrl = "/coach";
    if (
      user.role === "group_coach" ||
      user.role === "work_coach" ||
      user.role === "associate"
    ) {
      redirectUrl = "/groupCoach";
    } else if (user.role === "administrator") {
      redirectUrl = "/admin";
    } else if (user.role === "accounting") {
      redirectUrl = "/accounting";
    } else if (user.role === "supervisor") {
      redirectUrl = "/supervisor";
    }

    return redirect(redirectUrl, {
      headers: { "Set-Cookie": await sessionStorage.commitSession(session) },
    });
  } catch (error) {
    Sentry.captureException(error);
    return { error: "Virheellinen sähköposti tai salasana" };
  }
};

export const loader: LoaderFunction = async ({ request }) => {
  const passwordLoginEnabled = isPasswordLoginEnabled();
  const apiUrl = process.env.API_URL;

  if (!apiUrl) {
    throw new Error("Missing API_URL");
  }

  const url = new URL(request.url);
  const strongAuthErrorCode = url.searchParams.get("strong_auth_error");
  const strongAuthErrorMessageFromQuery =
    strongAuthErrorMessage(strongAuthErrorCode);

  // If password login is disabled, we still show the login page but without the password form.
  // The logic to redirect directly to strong-auth is removed as we now have the button on this page.

  const session = await sessionStorage.getSession(
    request.headers.get("cookie")
  );

  const user = session.get("user");

  if (user) {
    let redirectUrl = "/coach";
    if (
      user.role === "group_coach" ||
      user.role === "work_coach" ||
      user.role === "associate"
    ) {
      redirectUrl = "/groupCoach";
    } else if (user.role === "administrator") {
      redirectUrl = "/admin";
    } else if (user.role === "accounting") {
      redirectUrl = "/accounting";
    } else if (user.role === "supervisor") {
      redirectUrl = "/supervisor";
    }

    throw redirect(redirectUrl);
  }

  return {
    passwordLoginEnabled,
    strongAuthErrorMessage: strongAuthErrorMessageFromQuery,
    apiUrl,
  } satisfies LoaderData;
};

export default function LoginPage(): ReactNode {
  const loaderData = useLoaderData<LoaderData>();
  const actionData = useActionData<{ error?: string }>();
  const error = actionData?.error ?? loaderData.strongAuthErrorMessage;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <LoginForm
        error={error}
        apiUrl={loaderData.apiUrl}
        passwordLoginEnabled={loaderData.passwordLoginEnabled}
      />
    </div>
  );
}
